import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import client from '../../../../../API';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Step,
  Stepper,
  StepLabel
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Report as ReportIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { replaceConsoleLog, replaceConsoleError } from '../../../../../Utils/toastNotifications';

const CreatorNuke = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [nukeDialogOpen, setNukeDialogOpen] = useState(false);
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [reason, setReason] = useState('');
  const [forceDelete, setForceDelete] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  // Query for nuke history
  const {
    data: nukeHistory,
    isLoading: historyLoading,
  } = useQuery({
    queryKey: ['creator-nuke-history'],
    queryFn: () => client.creators.getNukeHistory(),
    refetchOnWindowFocus: false
  });

  // Mutation to preview nuke
  const { mutate: previewNuke, isLoading: previewLoading } = useMutation(
    (identifier) => client.creators.previewNuke(identifier),
    {
      onSuccess: (data) => {
        replaceConsoleLog('Preview nuke response', data);
        
        if (!data) {
          toast.error('Invalid response from server');
          return;
        }
        
        setPreviewData(data);
        
        if (data.multiple_matches) {
          toast.warning('Multiple creators found', {
            description: `Found ${data.creators?.length || 0} creators. Please use specific creator ID.`
          });
        } else if (data.creator?.creator) {
          toast.success('Creator found', {
            description: `Found creator ${data.creator.creator} with ${data.total_records_affected || 0} related records.`
          });
          setActiveStep(1);
        } else {
          toast.error('Invalid creator data received');
          replaceConsoleError('Invalid preview data structure', data);
        }
      },
      onError: (error) => {
        replaceConsoleError('Preview nuke error', error);
        toast.error('Search failed', {
          description: error.response?.data?.error || error.message
        });
        setPreviewData(null);
      }
    }
  );

  // Mutation to nuke creator
  const { mutate: nukeCreator, isLoading: nukeLoading } = useMutation(
    (nukeData) => client.creators.nukeCreator(nukeData),
    {
      onSuccess: (result) => {
        toast.success('Creator nuked successfully', {
          description: `Deleted ${result.summary.total_records_deleted} records for ${result.summary.creator_id}`
        });
        setNukeDialogOpen(false);
        resetForm();
      },
      onError: (error) => {
        toast.error('Nuke failed', {
          description: error.response?.data?.error || error.message
        });
      }
    }
  );

  const resetForm = () => {
    setSearchTerm('');
    setPreviewData(null);
    setConfirmationPhrase('');
    setReason('');
    setForceDelete(false);
    setActiveStep(0);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    previewNuke(searchTerm.trim());
  };

  const handleOpenNukeDialog = () => {
    if (!previewData || previewData.multiple_matches || !previewData.creator?.creator) {
      toast.error('Please select a specific creator first');
      return;
    }
    setNukeDialogOpen(true);
    setReason('Admin data cleanup - creator no longer needed');
  };

  const handleNuke = () => {
    if (!previewData?.creator?.creator) {
      toast.error('Creator data is missing');
      return;
    }

    const expectedPhrase = `DELETE ${previewData.creator.creator} PERMANENTLY`;
    
    if (confirmationPhrase !== expectedPhrase) {
      toast.error('Confirmation phrase incorrect', {
        description: `Please type exactly: ${expectedPhrase}`
      });
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for the deletion');
      return;
    }

    nukeCreator({
      creator_id: previewData.creator.creator,
      confirmation_phrase: confirmationPhrase,
      reason: reason,
      force_delete: forceDelete
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'info';
      default: return 'default';
    }
  };

  const renderMultipleMatches = () => {
    if (!previewData?.multiple_matches) return null;

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" color="warning.main" sx={{ mb: 2 }}>
            Multiple Creators Found
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Found {previewData.creators.length} creators matching "{previewData.identifier_searched}".
            Please use a specific creator ID.
          </Typography>
          
          <Grid container spacing={2}>
            {previewData.creators.map((creator) => (
              <Grid item xs={12} md={6} key={creator.creator}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{creator.display_name || creator.creator}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {creator.creator}
                    </Typography>
                    <Typography variant="body2">
                      Email: {creator.email || creator.payout_email || 'None'}
                    </Typography>
                    <Typography variant="body2">
                      Phone: {creator.phone_number || 'None'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => setSearchTerm(creator.creator)}
                    >
                      Use This Creator
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderPreviewData = () => {
    if (!previewData || previewData.multiple_matches || !previewData.creator) return null;

    const { creator, related_data = {}, total_records_affected = 0, warnings = [], severity = 'UNKNOWN' } = previewData;

    // Additional safety check
    if (!creator.creator) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Invalid creator data received. Please try searching again.
        </Alert>
      );
    }

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">Nuclear Deletion Preview</Typography>
            <Chip 
              label={`${severity} IMPACT`}
              color={getSeverityColor(severity)}
              icon={<WarningIcon />}
            />
            <Chip 
              label={`${total_records_affected} Records`}
              color="error"
              variant="outlined"
            />
          </Box>

          {warnings.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Warnings:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Creator Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="error.main">Target Creator</Typography>
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>ID:</strong> {creator.creator || 'N/A'}</Typography>
                    <Typography><strong>Display Name:</strong> {creator.display_name || 'None'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Email:</strong> {creator.email || 'None'}</Typography>
                    <Typography><strong>Phone:</strong> {creator.phone_number || 'None'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography><strong>Status:</strong> {creator.status || 'None'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Related Data Breakdown */}
          <Typography variant="h6" color="error.main" sx={{ mb: 2 }}>
            Data to be Deleted
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Creator Account"
                    secondary={related_data.creator_user?.exists ? 
                      `Active account with ${related_data.creator_user.has_google_token ? 'Google' : 'no'} OAuth` :
                      'No user account'
                    }
                  />
                  <Chip label="1" size="small" color="error" />
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Payouts"
                    secondary={`Total: ${related_data.payouts?.total_amount || 0}`}
                  />
                  <Chip label={related_data.payouts?.count || 0} size="small" color="error" />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Withdrawals"
                    secondary={`Total: ${related_data.withdrawals?.total_amount || 0}`}
                  />
                  <Chip label={related_data.withdrawals?.count || 0} size="small" color="error" />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Transactions"
                    secondary={`Total: ${related_data.transactions?.total_amount || 0}`}
                  />
                  <Chip label={related_data.transactions?.count || 0} size="small" color="error" />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Creator Invoices"
                    secondary="Payout invoices"
                  />
                  <Chip label={related_data.creator_invoices?.count || 0} size="small" color="error" />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Demo Data"
                    secondary="Platform analytics"
                  />
                  <Chip label={related_data.demo_data?.count || 0} size="small" color="error" />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Payment Info"
                    secondary={`${related_data.payment_info?.wallets_count || 0} wallets, ${related_data.payment_info?.tax_info_count || 0} tax records`}
                  />
                  <Chip 
                    label={
                      (related_data.payment_info?.wallets_count || 0) + 
                      (related_data.payment_info?.tax_info_count || 0)
                    } 
                    size="small" 
                    color="error" 
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Campaign References"
                    secondary="References in campaigns"
                  />
                  <Chip label={related_data.campaign_references?.count || 0} size="small" color="warning" />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          {/* Action Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<DeleteIcon />}
              onClick={handleOpenNukeDialog}
              disabled={total_records_affected === 0}
            >
              NUCLEAR DELETE CREATOR
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Warning Banner */}
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ⚠️ DESTRUCTIVE OPERATION - NUCLEAR DATA DELETION ⚠️
        </Typography>
        <Typography>
          This tool permanently deletes ALL creator data and related records. 
          This action is IRREVERSIBLE and should only be used for complete data cleanup.
          All payouts, withdrawals, transactions, and associated data will be permanently destroyed.
        </Typography>
      </Alert>

      {/* Step Indicator */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        <Step>
          <StepLabel>Search Creator</StepLabel>
        </Step>
        <Step>
          <StepLabel>Review Impact</StepLabel>
        </Step>
        <Step>
          <StepLabel>Nuclear Deletion</StepLabel>
        </Step>
      </Stepper>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Step 1: Find Creator to Delete
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Search by creator ID, username, email, or phone number. System will find all matching creators.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Creator ID, Email, or Phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="e.g., john_doe, john@example.com, +1234567890"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={previewLoading || !searchTerm.trim()}
              startIcon={previewLoading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results */}
      {renderMultipleMatches()}
      {renderPreviewData()}

      {/* Nuke History */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography>Nuclear Deletion History</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {historyLoading ? (
            <CircularProgress />
          ) : (
            <Typography color="text.secondary">
              {nukeHistory?.message || 'No nuclear deletion history available. Check server logs for deletion records.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Nuclear Deletion Confirmation Dialog */}
      <Dialog
        open={nukeDialogOpen}
        onClose={() => setNukeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            CONFIRM NUCLEAR DELETION
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              FINAL WARNING - POINT OF NO RETURN
            </Typography>
            <Typography>
              You are about to permanently delete ALL data for creator{' '}
              <strong>{previewData?.creator?.creator || 'UNKNOWN'}</strong> including:
            </Typography>
            <ul>
              <li>{previewData?.total_records_affected || 0} total database records</li>
              <li>All financial transactions and history</li>
              <li>All payment information and tax records</li>
              <li>All campaign associations and analytics</li>
              <li>User account and authentication tokens</li>
            </ul>
            <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
              THIS CANNOT BE UNDONE.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Reason for Deletion"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label="Confirmation Phrase"
            value={confirmationPhrase}
            onChange={(e) => setConfirmationPhrase(e.target.value)}
            placeholder={previewData?.creator?.creator ? `DELETE ${previewData.creator.creator} PERMANENTLY` : 'DELETE CREATOR_ID PERMANENTLY'}
            sx={{ mb: 2 }}
            helperText={`Type exactly: DELETE ${previewData?.creator?.creator || 'CREATOR_ID'} PERMANENTLY`}
            required
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
                color="error"
              />
            }
            label="Force delete even with active transactions (DANGEROUS)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNukeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleNuke}
            variant="contained"
            color="error"
            disabled={
              !confirmationPhrase || 
              !reason.trim() || 
              nukeLoading ||
              confirmationPhrase !== `DELETE ${previewData?.creator?.creator} PERMANENTLY`
            }
            startIcon={nukeLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {nukeLoading ? 'NUKING...' : 'NUCLEAR DELETE'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorNuke;